import * as PIXI from 'pixi.js';
import { Wire, WireBundle as WireBundleData, WireBundleSegment, WireBow } from '../types';
import { BUNDLE_GEOMETRY, COLORS, DIMENSIONS, LABEL_RESOLUTION } from '../utils/constants';
import { formatLength } from '../utils/length';

/** Height (px) of the badge that carries a section's wire count and length. */
const BADGE_HEIGHT = 20;
/** Horizontal padding inside the badge and the gap around its divider. */
const BADGE_PAD_X = 8;
const BADGE_GAP = 7;
/** Two left presses within this window on a badge count as a double-click. */
const DOUBLE_CLICK_MS = 300;
/** Colour of a highlighted wire, section or bow. */
const HIGHLIGHT_COLOR = 0xffff00;
/** Badge border: dark by default and when there is no layout to compare against. */
const BADGE_BORDER_DEFAULT = 0x2d3436;
/** Badge border: grey when the length matches the calibrated layout (auto). */
const BADGE_BORDER_AUTO = 0xb2bec3;
/** Badge border: light amber when the length has been overwritten off the layout. */
const BADGE_BORDER_OVERWRITTEN = 0xf3b84e;

/** Parses a "#rrggbb" wire colour to a PIXI numeric colour, defaulting to the trunk grey. */
function toPixiColor(color?: string): number {
  return parseInt((color ?? COLORS.WIRE_BUNDLE).replace('#', ''), 16);
}

export class WireBundle extends PIXI.Container {
  private bundleData: WireBundleData;
  private graphics: PIXI.Graphics;
  /** One badge per section, carrying its wire count and length. */
  private segmentBadges: PIXI.Container[] = [];
  private isHighlighted: boolean = false;
  /** When highlighted, the single wire whose stubs are lit; undefined lights all. */
  private highlightedWireId?: string;
  private hoverTimeout: NodeJS.Timeout | null = null;
  private lastEvent: any = null;

  constructor(bundleData: WireBundleData) {
    super();
    
    this.bundleData = bundleData;
    this.graphics = new PIXI.Graphics();
    this.addChild(this.graphics);
    this.setupInteraction();
    this.render();
  }

  private setupInteraction(): void {
    // Jumper bundles are decorative bows with no section; keep them out of hit
    // testing so their bounding box does not swallow clicks meant for the canvas.
    if (this.bundleData.segments.length === 0) {
      this.eventMode = 'none';
      return;
    }

    this.eventMode = 'static';
    this.cursor = 'pointer';

    // Set up hover events
    this.on('pointerenter', (event) => {
      this.startHoverTimer(event);
    });
    
    this.on('pointerleave', () => {
      this.clearHoverTimer();
      this.emit('hideTooltip');
    });
    
    this.on('pointermove', (event) => {
      // Reset timer on mouse movement within bundle
      this.clearHoverTimer();
      this.startHoverTimer(event);
    });
    
    // Set up drag events for bundle manipulation
    this.setupDragInteraction();
  }

  private setupDragInteraction(): void {
    let isDragging = false;
    
    this.on('pointerdown', (event) => {
      if (event.button === 0) { // Left click only
        event.stopPropagation();
        isDragging = true;
        
        this.clearHoverTimer();
        this.emit('hideTooltip');
        this.emit('bundleDragStart', this.bundleData.id, event.global.x, event.global.y);
        
        // Set up global mouse tracking for reliable drag handling
        const handleGlobalMove = (e: MouseEvent) => {
          if (isDragging) {
            // Convert DOM coordinates to canvas-relative coordinates
            const canvas = this.getCanvas();
            if (canvas) {
              const rect = canvas.getBoundingClientRect();
              const canvasX = e.clientX - rect.left;
              const canvasY = e.clientY - rect.top;
              this.emit('bundleDragMove', this.bundleData.id, canvasX, canvasY);
            }
          }
        };
        
        const handleGlobalUp = (e: MouseEvent) => {
          if (isDragging) {
            isDragging = false;
            // Convert DOM coordinates to canvas-relative coordinates
            const canvas = this.getCanvas();
            if (canvas) {
              const rect = canvas.getBoundingClientRect();
              const canvasX = e.clientX - rect.left;
              const canvasY = e.clientY - rect.top;
              this.emit('bundleDragEnd', this.bundleData.id, canvasX, canvasY);
            }
            document.removeEventListener('mousemove', handleGlobalMove);
            document.removeEventListener('mouseup', handleGlobalUp);
          }
        };
        
        document.addEventListener('mousemove', handleGlobalMove);
        document.addEventListener('mouseup', handleGlobalUp);
      }
    });
  }

  private startHoverTimer(event: any): void {
    this.lastEvent = event;
    this.clearHoverTimer();
    this.hoverTimeout = setTimeout(() => {
      this.showTooltip();
    }, 500); // 500ms delay as specified
  }

  private clearHoverTimer(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  private showTooltip(): void {
    const wireList = this.bundleData.wires.map(wire => 
      `${wire.name}${wire.note ? ' - ' + wire.note : ''}`
    ).join('\n');
    
    if (this.lastEvent) {
      this.emit('showTooltip', wireList, this.lastEvent.global.x, this.lastEvent.global.y);
    }
  }

  updateBundleData(bundleData: WireBundleData): void {
    this.bundleData = bundleData;
    this.render();
  }

  private render(): void {
    this.graphics.clear();
    this.clearSegmentBadges();

    this.bundleData.segments.forEach(segment => {
      this.drawSegment(segment);
    });

    (this.bundleData.bows ?? []).forEach(bow => {
      this.drawBow(bow);
    });
  }

  private drawSegment(segment: WireBundleSegment): void {
    const width = this.isHighlighted ? DIMENSIONS.WIRE_THICKNESS + 2 : DIMENSIONS.WIRE_THICKNESS;

    // Individual wire breakouts first, so the trunk joins over them. Only the
    // hovered wire's own breakout lights up, leaving its siblings in the tree
    // at their normal colour.
    (segment.stubs ?? []).forEach(stub => {
      const lit = this.isStubLit(stub.wireId);
      this.graphics.moveTo(stub.from.x, stub.from.y);
      this.graphics.lineTo(stub.to.x, stub.to.y);
      this.graphics.stroke({
        width: lit ? DIMENSIONS.WIRE_THICKNESS + 2 : DIMENSIONS.WIRE_THICKNESS,
        color: lit ? HIGHLIGHT_COLOR : toPixiColor(stub.color)
      });
    });

    const trunkColor = this.isHighlighted ? HIGHLIGHT_COLOR : toPixiColor(segment.color);
    this.graphics.moveTo(segment.startPoint.x, segment.startPoint.y);
    this.graphics.lineTo(segment.endPoint.x, segment.endPoint.y);
    this.graphics.stroke({ width, color: trunkColor });

    this.addSegmentBadge(segment);
  }

  /** Whether a breakout stub should be highlighted: only the hovered wire's own. */
  private isStubLit(wireId: string): boolean {
    return this.isHighlighted && (this.highlightedWireId === undefined || this.highlightedWireId === wireId);
  }

  /** Draws a same-connector jumper as an arc bulging out past the connector's left edge. */
  private drawBow(bow: WireBow): void {
    const width = this.isHighlighted ? DIMENSIONS.WIRE_THICKNESS + 2 : DIMENSIONS.WIRE_THICKNESS;
    const controlX = Math.min(bow.from.x, bow.to.x) - BUNDLE_GEOMETRY.BOW_BULGE;
    const controlY = (bow.from.y + bow.to.y) / 2;

    this.graphics.moveTo(bow.from.x, bow.from.y);
    this.graphics.quadraticCurveTo(controlX, controlY, bow.to.x, bow.to.y);
    this.graphics.stroke({ width, color: this.isHighlighted ? HIGHLIGHT_COLOR : toPixiColor(bow.color) });
  }

  /**
   * Draws the section badge at the section midpoint: a rounded rectangle aligned
   * with the section's direction, carrying the wire count and the section length
   * side by side (a muted "? mm" invites measuring it). The badge is
   * double-clickable to open the section properties.
   */
  private addSegmentBadge(segment: WireBundleSegment): void {
    const midX = (segment.startPoint.x + segment.endPoint.x) / 2;
    const midY = (segment.startPoint.y + segment.endPoint.y) / 2;

    const badge = new PIXI.Container();
    badge.x = midX;
    badge.y = midY;
    badge.rotation = this.badgeAngle(segment);

    const countText = new PIXI.Text(segment.wireCount.toString(), new PIXI.TextStyle({
      fontSize: 11,
      fontWeight: 'bold',
      fill: 0x2d3436
    }));
    countText.resolution = LABEL_RESOLUTION;

    const measured = segment.length > 0;
    const lengthText = new PIXI.Text(measured ? formatLength(segment.length) : '? mm', new PIXI.TextStyle({
      fontSize: 10,
      fontWeight: measured ? 'bold' : 'normal',
      fill: measured ? 0x2d3436 : 0xb2bec3
    }));
    lengthText.resolution = LABEL_RESOLUTION;

    const contentWidth = countText.width + BADGE_GAP * 2 + 1 + lengthText.width;
    const width = contentWidth + BADGE_PAD_X * 2;

    // A grey border marks a length that still matches the layout, light amber a
    // length overwritten off it; the default dark border is used when there is no
    // layout to compare against.
    const borderColor =
      segment.lengthStatus === 'auto' ? BADGE_BORDER_AUTO :
      segment.lengthStatus === 'overwritten' ? BADGE_BORDER_OVERWRITTEN :
      BADGE_BORDER_DEFAULT;

    const bg = new PIXI.Graphics();
    bg.roundRect(-width / 2, -BADGE_HEIGHT / 2, width, BADGE_HEIGHT, 5);
    bg.fill(0xffffff);
    bg.stroke({ width: 1, color: borderColor });
    badge.addChild(bg);

    let cursor = -contentWidth / 2;
    countText.anchor.set(0, 0.5);
    countText.x = cursor;
    badge.addChild(countText);
    cursor += countText.width + BADGE_GAP;

    // Thin divider separating the wire count from the length.
    const divider = new PIXI.Graphics();
    divider.moveTo(cursor, -BADGE_HEIGHT / 2 + 4);
    divider.lineTo(cursor, BADGE_HEIGHT / 2 - 4);
    divider.stroke({ width: 1, color: 0xdfe4ea });
    badge.addChild(divider);
    cursor += 1 + BADGE_GAP;

    lengthText.anchor.set(0, 0.5);
    lengthText.x = cursor;
    badge.addChild(lengthText);

    this.attachBadgeInteraction(badge, segment, width);

    this.addChild(badge);
    this.segmentBadges.push(badge);
  }

  /** Angle to lay the badge along, kept upright so the text never reads upside down. */
  private badgeAngle(segment: WireBundleSegment): number {
    const dx = segment.endPoint.x - segment.startPoint.x;
    const dy = segment.endPoint.y - segment.startPoint.y;
    let angle = Math.atan2(dy, dx);
    if (angle > Math.PI / 2) {
      angle -= Math.PI;
    } else if (angle < -Math.PI / 2) {
      angle += Math.PI;
    }
    return angle;
  }

  /** Makes a section badge its own control: it swallows presses and opens the section on double-click. */
  private attachBadgeInteraction(badge: PIXI.Container, segment: WireBundleSegment, width: number): void {
    badge.eventMode = 'static';
    badge.cursor = 'pointer';
    badge.hitArea = new PIXI.Rectangle(-width / 2, -BADGE_HEIGHT / 2, width, BADGE_HEIGHT);

    let lastTapAt = 0;
    badge.on('pointerdown', (event) => {
      if (event.button !== 0) {
        return;
      }
      // Keep the press off the bundle beneath so it neither drags nor adds a node.
      event.stopPropagation();
      const now = Date.now();
      const isDoubleClick = now - lastTapAt < DOUBLE_CLICK_MS;
      lastTapAt = now;
      if (isDoubleClick) {
        this.emit('sectionDoubleClick', segment.aId, segment.bId, event.global.x, event.global.y);
      }
    });
  }

  private clearSegmentBadges(): void {
    this.segmentBadges.forEach(badge => this.removeChild(badge));
    this.segmentBadges = [];
  }

  /**
   * Highlights this bundle. When `wireId` is given, only that wire's breakout
   * stubs light up (the rest of the tree keeps its colour); the shared trunk is
   * always highlighted to trace the section the wire runs through.
   */
  setHighlighted(highlighted: boolean, wireId?: string): void {
    this.isHighlighted = highlighted;
    this.highlightedWireId = highlighted ? wireId : undefined;
    this.render();
  }

  getSegmentAt(x: number, y: number): WireBundleSegment | null {
    for (const segment of this.bundleData.segments) {
      if (this.isPointOnSegment(x, y, segment)) {
        return segment;
      }
    }
    return null;
  }

  private isPointOnSegment(
    x: number, 
    y: number, 
    segment: WireBundleSegment, 
    tolerance: number = 5
  ): boolean {
    const dx = segment.endPoint.x - segment.startPoint.x;
    const dy = segment.endPoint.y - segment.startPoint.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    
    if (length === 0) return false;
    
    const t = Math.max(0, Math.min(1, 
      ((x - segment.startPoint.x) * dx + (y - segment.startPoint.y) * dy) / (length * length)
    ));
    
    const projX = segment.startPoint.x + t * dx;
    const projY = segment.startPoint.y + t * dy;
    
    const distance = Math.sqrt((x - projX) ** 2 + (y - projY) ** 2);
    return distance <= tolerance;
  }

  getBundleData(): WireBundleData {
    return this.bundleData;
  }

  getWires(): Wire[] {
    return this.bundleData.wires;
  }

  private getCanvas(): HTMLCanvasElement | null {
    // Find the canvas element by traversing up to find the renderer
    let current: PIXI.Container | null = this.parent;
    while (current && current.parent) {
      current = current.parent;
    }
    
    if (current && (current as any).renderer) {
      return (current as any).renderer.canvas;
    }
    
    // Fallback: look for canvas in document
    const canvases = document.getElementsByTagName('canvas');
    if (canvases.length > 0) {
      return canvases[0];
    }
    
    return null;
  }
}